/**
 * Region-aware phone formatting utilities.
 *
 * New functions (react-phone-number-input based):
 *   getDefaultCountryISO() - Auto-detect default country ISO code
 *   toE164(dbValue)        - DB format -> library format (add "+")
 *   fromE164(e164Value)    - Library format -> DB format (remove "+")
 *   localDigitsFromE164()  - Extract local digits from E.164
 *   formatPhoneDisplay()   - Universal display formatting
 *
 * Legacy functions kept for backward compatibility:
 *   SUPPORTED_COUNTRY_CODES, getCountryCodeDigits, getDefaultCountry,
 *   buildTelefoneCompleto, buildWhatsAppLink, formatPhone,
 *   getPhonePrefix, getPhoneMask, getPhoneLimits
 */
import { formatPhoneNumberIntl, parsePhoneNumber, getCountries, getCountryCallingCode } from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en';
import regionConfig from '../config/regionConfig';

// ─────────────────────────────────────────────────────────
// NEW: react-phone-number-input integration
// ─────────────────────────────────────────────────────────

/**
 * Get the default country as ISO 3166-1 alpha-2 code.
 * Priority: 1) company.country in localStorage (mais específico),
 *           2) regionConfig.identity.defaultCountry (região do deploy — sinal forte),
 *           3) browser locale (fraco/instável — só como fallback),
 *           4) fallback 'BR'.
 * A região vem ANTES do locale: um navegador en-US no Líbano resolvia 'US'
 * (bug reportado). No deploy da região o default agora bate com o país das lojas.
 * @returns {string} ISO country code, e.g. "BR", "LB", "FR"
 */
export function getDefaultCountryISO() {
  // 1. Company in localStorage (logged-in user with specific company)
  try {
    const raw = localStorage.getItem('empresa');
    if (raw) {
      const empresa = JSON.parse(raw);
      if (empresa?.country) {
        const iso = empresa.country.toUpperCase();
        if (/^[A-Z]{2}$/.test(iso)) return iso;
      }
    }
  } catch { /* ignore */ }

  // 2. Região do deploy (regionConfig)
  try {
    const iso = regionConfig?.identity?.defaultCountry?.toUpperCase();
    if (iso && /^[A-Z]{2}$/.test(iso)) return iso;
  } catch { /* ignore */ }

  // 3. Browser locale (e.g. "pt-BR" -> "BR", "fr-FR" -> "FR", "ar-LB" -> "LB")
  try {
    const lang = navigator.language || navigator.languages?.[0];
    if (lang) {
      const parts = lang.split('-');
      if (parts.length >= 2) {
        const countryISO = parts[parts.length - 1].toUpperCase();
        if (/^[A-Z]{2}$/.test(countryISO)) return countryISO;
      }
    }
  } catch { /* ignore */ }

  // 4. Fallback
  return 'BR';
}

/**
 * Países fixados no topo do seletor (região + vizinhos comuns). Usado pelo CountrySelect.
 * @returns {string[]} ISOs, ex: ['LB','SY','CY',...] ou ['BR']
 */
export function getPreferredCountries() {
  const list = regionConfig?.identity?.preferredCountries;
  if (Array.isArray(list) && list.length > 0) {
    return list.map(c => String(c).toUpperCase()).filter(c => /^[A-Z]{2}$/.test(c));
  }
  return [getDefaultCountryISO()];
}

/**
 * Convert DB format (no "+") to library E.164 format (with "+").
 * Handles: "5511987654321" -> "+5511987654321"
 *          "11987654321"   -> "+5511987654321" (prepends detected/default code)
 *          ""              -> undefined
 * @param {string} dbValue - Phone digits from database
 * @returns {string|undefined} E.164 with "+" or undefined if empty
 */
export function toE164(dbValue) {
  if (!dbValue) return undefined;
  const digits = String(dbValue).replace(/\D/g, '');
  if (!digits) return undefined;

  // If starts with "+", already E.164
  if (String(dbValue).startsWith('+')) return dbValue;

  const withPlus = `+${digits}`;

  // Quick check against our known country codes (works even for test numbers)
  const detected = detectCountryCode(digits);
  if (detected) return withPlus;

  // Try parsing with "+" prefix - if valid, it already has country code
  try {
    const parsed = parsePhoneNumber(withPlus);
    if (parsed && parsed.isValid()) return withPlus;
  } catch { /* continue */ }

  // Try prepending default country code via the library
  const defaultISO = getDefaultCountryISO();
  try {
    const parsed = parsePhoneNumber(digits, defaultISO);
    if (parsed && parsed.isValid()) return parsed.number; // E.164 format
  } catch { /* continue */ }

  // Ultimate fallback: just add "+"
  return withPlus;
}

/**
 * Convert library E.164 format (with "+") to DB format (without "+").
 * "+5511987654321" -> "5511987654321"
 * @param {string|undefined} e164Value - E.164 value from library
 * @returns {string} Digits only (no "+")
 */
export function fromE164(e164Value) {
  if (!e164Value) return '';
  return String(e164Value).replace(/^\+/, '');
}

/**
 * Extract local digits (without country code) from E.164 value.
 * "+5511987654321" -> "11987654321"
 * @param {string|undefined} e164Value - E.164 value
 * @returns {string} Local phone digits
 */
export function localDigitsFromE164(e164Value) {
  if (!e164Value) return '';
  try {
    const parsed = parsePhoneNumber(String(e164Value));
    if (parsed) return parsed.nationalNumber;
  } catch { /* fallthrough */ }
  // Fallback: strip "+" and return all digits
  return String(e164Value).replace(/\D/g, '');
}

/**
 * Format any phone number for display using libphonenumber-js.
 * Universal — works for ALL countries, not just BR/LB.
 *
 * Accepts: E.164 with "+"  ("+5511987654321")
 *          E.164 without "+" ("5511987654321")
 *          Local digits      ("11987654321")
 *
 * Returns: "+55 11 98765-4321" (international format)
 *
 * @param {string} phone - Phone number in any format
 * @returns {string} Formatted phone number or original if unparseable
 */
export function formatPhoneDisplay(phone) {
  if (!phone) return '';
  const raw = String(phone).trim();
  if (!raw) return '';

  // 1. Try as-is (might already have "+")
  if (raw.startsWith('+')) {
    const formatted = formatPhoneNumberIntl(raw);
    if (formatted) return formatted;
  }

  // 2. Try with "+" prefix (DB format without "+")
  const digits = raw.replace(/\D/g, '');
  if (digits) {
    const withPlus = `+${digits}`;
    const formatted = formatPhoneNumberIntl(withPlus);
    if (formatted) return formatted;
  }

  // 3. Try as local number with default country
  if (digits) {
    const defaultISO = getDefaultCountryISO();
    try {
      const parsed = parsePhoneNumber(digits, defaultISO);
      if (parsed) {
        const formatted = parsed.formatInternational();
        if (formatted) return formatted;
      }
    } catch { /* fallthrough */ }
  }

  // 4. Fallback: return cleaned digits
  return raw;
}

// ─────────────────────────────────────────────────────────
// COUNTRY LIST: All countries for address/country selectors
// ─────────────────────────────────────────────────────────

const COUNTRY_FLAG_OFFSET = 0x1F1E6 - 65; // 'A' = 65

function countryToFlag(iso) {
  return String.fromCodePoint(
    iso.charCodeAt(0) + COUNTRY_FLAG_OFFSET,
    iso.charCodeAt(1) + COUNTRY_FLAG_OFFSET
  );
}

let _cachedAllCountries = null;

/**
 * Get a sorted list of ALL countries with ISO code, name, flag, and calling code.
 * Uses react-phone-number-input's built-in data (~240 countries).
 * Sorted alphabetically by English name.
 * @returns {Array<{ country: string, name: string, flag: string, code: string }>}
 */
export function getAllCountries() {
  if (_cachedAllCountries) return _cachedAllCountries;

  const countries = getCountries();
  _cachedAllCountries = countries
    .map((iso) => ({
      country: iso,
      name: en[iso] || iso,
      flag: countryToFlag(iso),
      code: getCountryCallingCode(iso),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return _cachedAllCountries;
}

// ─────────────────────────────────────────────────────────
// LEGACY: Kept for backward compatibility during migration
// ─────────────────────────────────────────────────────────

/**
 * Supported country codes with metadata for phone input dropdown.
 * @deprecated Used by ConfiguracaoInicial and enderecoEstabelecimento for country dropdowns.
 * Phone input now uses react-phone-number-input with all countries.
 */
export const SUPPORTED_COUNTRY_CODES = [
  { code: '55',  country: 'BR', name: 'Brazil',   flag: '\u{1F1E7}\u{1F1F7}', placeholder: '11987654321',  minLocal: 10, maxLocal: 11 },
  { code: '90',  country: 'TR', name: 'Turkey',   flag: '\u{1F1F9}\u{1F1F7}', placeholder: '5321234567',   minLocal: 10, maxLocal: 10 },
  { code: '357', country: 'CY', name: 'Cyprus',   flag: '\u{1F1E8}\u{1F1FE}', placeholder: '22345678',     minLocal: 8,  maxLocal: 8  },
  { code: '33',  country: 'FR', name: 'France',   flag: '\u{1F1EB}\u{1F1F7}', placeholder: '612345678',    minLocal: 9,  maxLocal: 9  },
  { code: '961', country: 'LB', name: 'Lebanon',  flag: '\u{1F1F1}\u{1F1E7}', placeholder: '1234567',      minLocal: 7,  maxLocal: 8  },
  { code: '49',  country: 'DE', name: 'Germany',  flag: '\u{1F1E9}\u{1F1EA}', placeholder: '3012345678',   minLocal: 6,  maxLocal: 11 },
  { code: '963', country: 'SY', name: 'Syria',    flag: '\u{1F1F8}\u{1F1FE}', placeholder: '912345678',    minLocal: 7,  maxLocal: 9  },
  { code: '962', country: 'JO', name: 'Jordan',   flag: '\u{1F1EF}\u{1F1F4}', placeholder: '79012345',     minLocal: 5,  maxLocal: 9  },
  { code: '964', country: 'IQ', name: 'Iraq',     flag: '\u{1F1EE}\u{1F1F6}', placeholder: '7901234567',   minLocal: 8,  maxLocal: 10 },
];

/**
 * Get the country code digits for the current region (without "+").
 * @returns {string} e.g. "55" or "961"
 */
export function getCountryCodeDigits() {
  return regionConfig.identity.phonePrefix.replace('+', '');
}

/**
 * Get the default country entry from SUPPORTED_COUNTRY_CODES.
 * @deprecated Use getDefaultCountryISO() for the new PhoneInput.
 */
export function getDefaultCountry() {
  try {
    const raw = localStorage.getItem('empresa');
    if (raw) {
      const empresa = JSON.parse(raw);
      if (empresa?.country) {
        const match = SUPPORTED_COUNTRY_CODES.find(
          c => c.country === empresa.country.toUpperCase()
        );
        if (match) return match;
      }
    }
  } catch { /* ignore parse errors */ }

  const code = getCountryCodeDigits();
  return SUPPORTED_COUNTRY_CODES.find(c => c.code === code) || SUPPORTED_COUNTRY_CODES[0];
}

/**
 * Detect country code from a digits-only phone number.
 * @param {string} digits - Phone number digits only
 * @returns {{ code: string, localNumber: string } | null}
 */
export function detectCountryCode(digits) {
  if (!digits) return null;
  const sorted = [...SUPPORTED_COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
  for (const entry of sorted) {
    if (digits.startsWith(entry.code) && digits.length > entry.code.length) {
      return { code: entry.code, localNumber: digits.slice(entry.code.length) };
    }
  }
  return null;
}

/**
 * Build a complete international phone number (digits only, no "+").
 * @deprecated Use fromE164() with the new PhoneInput value instead.
 */
export function buildTelefoneCompleto(localPhone, countryCode) {
  if (!localPhone) return '';
  const digits = String(localPhone).replace(/\D/g, '');
  if (!digits) return '';
  const detected = detectCountryCode(digits);
  if (detected) return digits;
  const prefix = countryCode || getCountryCodeDigits();
  return `${prefix}${digits}`;
}

/**
 * Build a WhatsApp link (wa.me) from a phone number.
 * @param {string} phone - International phone digits or local phone
 * @param {string} [message] - Optional pre-filled message
 * @returns {string} WhatsApp URL
 */
export function buildWhatsAppLink(phone, message) {
  if (!phone) return '';
  let digits = String(phone).replace(/\D/g, '');
  const detected = detectCountryCode(digits);
  if (!detected) {
    digits = `${getCountryCodeDigits()}${digits}`;
  }
  let url = `https://wa.me/${digits}`;
  if (message) {
    url += `?text=${encodeURIComponent(message)}`;
  }
  return url;
}

/**
 * Format a phone number according to the region.
 * @deprecated Use formatPhoneDisplay() for universal formatting.
 */
export function formatPhone(phone) {
  return formatPhoneDisplay(phone);
}

/**
 * Get the phone prefix for the current region.
 * @returns {string} Phone prefix (e.g. "+55" or "+961")
 */
export function getPhonePrefix() {
  return regionConfig.identity.phonePrefix;
}

/**
 * @deprecated Masks removed in favor of react-phone-number-input.
 */
export function getPhoneMask() {
  return regionConfig.identity.phoneMask;
}

/**
 * @deprecated The library handles validation internally.
 */
export function getPhoneLimits() {
  const code = getCountryCodeDigits();
  const country = SUPPORTED_COUNTRY_CODES.find(c => c.code === code);
  if (country) return { minLocal: country.minLocal, maxLocal: country.maxLocal };
  return { minLocal: 10, maxLocal: 11 };
}
